import { CollapsibleItem } from '@lmc/ui-kit'
import type { ToggleCollapseFn } from '@lmc/ui-kit/src/dataDisplay/CollapsibleItem/CollapsibleItem'
import DropdownIcon from '@lmc/ui-kit/src/dataDisplay/ListItem/DropdownIcon'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { type FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ClockInOutType } from 'screens/Rostering/hooks/useClockInOut'
import AllocationWindow from 'screens/Rostering/RotaBuilder/screens/AllocationWindow/AllocationWindow'
import {
  type ClockInTeamMember,
  type RotaEventInstance,
  RotaInstanceStatus
} from 'screens/Rostering/types'
import {
  getDateTimeInZone,
  TIME_FORMAT
} from 'screens/Rostering/utils.datetime'
import styled from 'styled-components'
import { MissingAllocationListItem } from '../MissingAllocationListItem'
import TeamMemberInfo from '../TeamMemberInfo'
import { ClockInOutDetails } from './ClockInOutDetails'
import { ClockInOutModal } from './ClockInOutModal'
import { Status } from './Status'

dayjs.extend(isoWeek)

type DrawerStatusProps = {
  revisionDetail: RotaEventInstance
}

export const DrawerStatus: FC<DrawerStatusProps> = ({ revisionDetail }) => {
  const { t } = useTranslation()

  const [clockInOutType, setClockInOutType] = useState<ClockInOutType>('out')

  const [clockInOutAction, setClockInOutAction] = useState<'edit' | 'manual'>(
    'manual'
  )

  const [teamMember, setTeamMember] = useState<ClockInTeamMember>()

  const [isClockInOutModalOpen, setIsClockInOutModalOpen] = useState(false)

  const openClockInOutModal = ({
    action,
    type,
    teamMember
  }: {
    action: 'edit' | 'manual'
    type: ClockInOutType
    teamMember: ClockInTeamMember
  }) => {
    setIsClockInOutModalOpen(true)
    setClockInOutType(type)
    setClockInOutAction(action)
    setTeamMember(teamMember)
  }

  const closeClockInOutModal = () => setIsClockInOutModalOpen(false)

  const [allocationWindowVisible, setAllocationWindowVisible] = useState(false)

  const openAllocationWindow = () => setAllocationWindowVisible(true)

  const closeAllocationWindow = () => setAllocationWindowVisible(false)

  const hoursStart = useMemo(
    () => getDateTimeInZone(revisionDetail?.startTime).format(TIME_FORMAT),
    [revisionDetail?.startTime, revisionDetail?.timezone]
  )

  const hoursEnd = useMemo(
    () => getDateTimeInZone(revisionDetail?.endTime).format(TIME_FORMAT),
    [revisionDetail?.endTime, revisionDetail?.timezone]
  )

  if (!revisionDetail) {
    return null
  }

  const isDraftEvent = revisionDetail?.status === RotaInstanceStatus.DRAFT

  const missingAllocationsCount = Math.max(
    0,
    (revisionDetail?.numberSupply ?? 0) -
      (revisionDetail?.teamMembers?.length ?? 0)
  )

  return (
    <>
      <DetailsWrapper>
        <HeadingWrapper>
          <Heading level={3}>{t('STATUS')}</Heading>
        </HeadingWrapper>
        <GrayWrapper>
          <HeadingWrapperMain>
            <InfoSection>
              <GrayText level={2}>{t('TIME_WINDOW')}</GrayText>
              <FlexiSection>
                <HoursSection>
                  <Text weight="bold">{`${hoursStart} - ${hoursEnd}`}</Text>
                </HoursSection>
                {revisionDetail?.flexiType && (
                  <Text level={2}>· {t('FLEXI')}</Text>
                )}
              </FlexiSection>
            </InfoSection>
            <InfoSection>
              <GrayText level={2}>{t('PLANNED_DURATION')}</GrayText>
              <Text weight="bold">
                {revisionDetail?.expectedDuration ?? '-'}
              </Text>
            </InfoSection>
            <InfoSection>
              <GrayText level={2}>{t('TEAM_MEMBERS_REQUIRED')}</GrayText>
              <Text weight="bold">
                {revisionDetail?.teamMembers?.length ?? '-'}/
                {revisionDetail?.numberSupply ?? '-'}{' '}
                <LowerCase>{t('TEAM_MEMBERS')}</LowerCase>
              </Text>
            </InfoSection>
          </HeadingWrapperMain>
        </GrayWrapper>
        {revisionDetail?.teamMembers?.map((item: any) => (
          <CollapsibleSection
            header={(toggleCollapse: ToggleCollapseFn, collapsed: boolean) => (
              <Wrapper onClick={toggleCollapse}>
                {/* @ts-ignore */}
                <TeamMemberInfo teamMember={item?.carer} />
                {/* @ts-ignore */}
                {!isDraftEvent && (
                  <Status revisionDetail={revisionDetail} teamMember={item} />
                )}
                <IconButton>
                  <RotatedDropdownIcon $isCollapsed={collapsed} />
                </IconButton>
              </Wrapper>
            )}
            defaultCollapsed
          >
            <CollapseWrapper>
              <ClockInOutDetails
                revisionDetail={revisionDetail}
                // @ts-ignore
                teamMember={item}
                openClockInOutModal={openClockInOutModal}
              />
            </CollapseWrapper>
          </CollapsibleSection>
        ))}
        {Array(missingAllocationsCount)
          .fill(null)
          .map((_, index) => (
            <MissingAllocationListItem
              openAllocationWindow={openAllocationWindow}
              // eslint-disable-next-line react/no-array-index-key
              key={`missing-allocation-${index}`}
            />
          ))}
      </DetailsWrapper>

      {isClockInOutModalOpen && (
        <ClockInOutModal
          open={isClockInOutModalOpen}
          onClose={closeClockInOutModal}
          revisionDetail={revisionDetail}
          action={clockInOutAction}
          type={clockInOutType}
          teamMember={teamMember}
        />
      )}

      {allocationWindowVisible && (
        <AllocationWindow
          allocationWindowVisible={allocationWindowVisible}
          onCancel={closeAllocationWindow}
          revisionDetail={revisionDetail}
        />
      )}
    </>
  )
}

const GrayText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const GrayWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background-color: ${(props) => props.theme.color.neutral.color10};
  border-radius: 20px;
`

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  color: ${(props) => props.theme.color.neutral.color3};
`

const HeadingWrapper = styled.div`
  display: flex;
  margin-top: 8px;
  align-items: center;
  justify-content: space-between;
`

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 185px;
`

const CollapsibleSection = styled(CollapsibleItem)`
  border: 1px solid ${({ theme }) => theme.color.neutral.color8};
  border-radius: 16px;
`

const LowerCase = styled.span`
  text-transform: lowercase;
`

const IconButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 40px;
  height: 40px;
  margin: 0;
  padding: 0;
  position: absolute;
  right: 0;

  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    width: 24px;
    height: 24px;
    margin: 0;
  }
`

const RotatedDropdownIcon = styled(DropdownIcon)<{ $isCollapsed?: boolean }>`
  transform: ${(props) =>
    props.$isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)'};
  transition: transform 0.3s ease-in-out;
`

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  cursor: pointer;
`
const CollapseWrapper = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const HeadingWrapperMain = styled.div`
  display: flex;
  justify-content: space-between;
`

const FlexiSection = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`
const HoursSection = FlexiSection

export default DrawerStatus
